'use client';

/**
 * A single AdSense ad unit.
 *
 * Behavior:
 *   - When `ads.adsense_enabled` is false in site config → renders nothing
 *   - When publisher ID isn't a valid format → renders nothing
 *   - When dev environment (NODE_ENV !== 'production') AND no `?ads=preview`
 *     query param → renders a labelled placeholder so designers can still
 *     see where ads will land. Stops AdSense from ever firing on localhost
 *     (Google flags repeated dev requests as policy violations).
 *
 * `slot` is the AdSense slot ID — created in the AdSense dashboard. Pass it
 * per placement so different ads on the same page have different IDs.
 *
 * The component renders Google's canonical `<ins class="adsbygoogle">` markup
 * and queues the impression via `window.adsbygoogle.push({})` after mount.
 */
import { useEffect, useRef } from 'react';
import { useSiteConfig } from '@/lib/site-config';
import { extractAdsensePublisherId, pushAdRequest } from '@/lib/adsense';

export type AdFormat = 'auto' | 'horizontal' | 'rectangle' | 'vertical' | 'fluid';

interface Props {
  /** AdSense slot id (from the dashboard). */
  slot: string;
  /** Display format. Default `auto` lets AdSense pick. */
  format?: AdFormat;
  /** When `true`, asks AdSense to honour the container's width on small screens. */
  responsive?: boolean;
  /** Optional CSS layout rules — e.g. limit max width on a wide hero. */
  style?: React.CSSProperties;
  /** Subtle "Sponsored" caption above the unit. AdSense's policies require ads
   *  to be visually distinguishable from organic content; the label helps. */
  label?: string;
}

export default function AdSlot({
  slot,
  format = 'auto',
  responsive = true,
  style,
  label = 'Sponsored',
}: Props) {
  const { config, ready } = useSiteConfig();
  const insRef = useRef<HTMLModElement | null>(null);
  const pushedRef = useRef(false);

  const enabled = !!config['ads.adsense_enabled'];
  const publisherId = extractAdsensePublisherId(String(config['ads.adsense_publisher_id'] ?? ''));
  const isDev = process.env.NODE_ENV !== 'production';

  useEffect(() => {
    if (!ready || !enabled || !publisherId || isDev) return;
    if (pushedRef.current) return;
    pushedRef.current = true;
    // Defer one tick so the <ins> is in the DOM before AdSense reads it.
    const id = window.setTimeout(pushAdRequest, 0);
    return () => window.clearTimeout(id);
  }, [ready, enabled, publisherId, isDev]);

  // Hide entirely while site config hasn't hydrated — better than flashing
  // either an empty slot or a placeholder that immediately disappears.
  if (!ready) return null;

  // Disabled or unconfigured? Render nothing on production. In dev, the
  // placeholder still helps designers see the layout.
  if (!enabled || !publisherId) {
    if (!isDev) return null;
    return (
      <DevPlaceholder
        slot={slot}
        reason={!enabled ? 'AdSense disabled in admin → Config' : 'Publisher ID not set'}
        style={style}
        label={label}
      />
    );
  }

  // Dev mode + valid config → still show the placeholder (Google policy).
  if (isDev) {
    return <DevPlaceholder slot={slot} reason="Dev environment — AdSense skipped" style={style} label={label} />;
  }

  return (
    <div style={{ display: 'block', ...style }} aria-label="Sponsored content">
      {label && (
        <div style={{
          fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em',
          color: 'var(--ink-3)', marginBottom: 6, fontWeight: 600, textAlign: 'center',
        }}>
          {label}
        </div>
      )}
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block', width: '100%' }}
        data-ad-client={`ca-${publisherId}`}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  );
}

/**
 * Visual placeholder shown only in dev. Lets engineers iterate on layout
 * without ever firing real AdSense requests from localhost.
 */
function DevPlaceholder({
  slot, reason, style, label,
}: { slot: string; reason: string; style?: React.CSSProperties; label?: string }) {
  return (
    <div style={style}>
      {label && (
        <div style={{
          fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em',
          color: 'var(--ink-3)', marginBottom: 6, fontWeight: 600, textAlign: 'center',
        }}>
          {label}
        </div>
      )}
      <div style={{
        border: '1px dashed var(--line)',
        borderRadius: 'var(--radius-md)',
        padding: '24px 18px',
        background: 'repeating-linear-gradient(135deg, transparent 0 12px, var(--bg-soft) 12px 13px)',
        textAlign: 'center',
        color: 'var(--ink-3)',
        fontSize: 12,
        lineHeight: 1.5,
      }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, marginBottom: 4, color: 'var(--ink-2)' }}>
          AdSense slot · {slot}
        </div>
        <div style={{ fontSize: 11 }}>{reason}</div>
      </div>
    </div>
  );
}
